import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import type { BannerFindInterface } from '@zaoblako/shared/modules/client/dtos/banner/banner-find.dto';
import { BannerFormDto, type BannerFormInterface } from '@zaoblako/shared/modules/client/dtos/banner/banner-form.dto';
import { BannerService } from '@zaoblako/web/core/services/client/banner.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { BannerHistoryComponent } from '@zaoblako/web/modules/client/dumbs/banner-form/banner-history/banner-history.component';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { ColorPickerComponent } from '@zaoblako/web/shared/dumbs/color-picker/color-picker.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ClientMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client-multi-select/client-multi-select.component';
import { RoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-multi-select/room-multi-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';

import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-banner-form',
  templateUrl: './banner-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    InputTextComponent,
    JoinPipe,
    DatepickerComponent,
    EditorComponent,
    ColorPickerComponent,
    ClientMultiSelectComponent,
    RoomMultiSelectComponent,
    TagModule,
    ButtonComponent,
    SpinnerComponent,
    BannerHistoryComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BannerFormComponent extends BaseFormComponent<BannerFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public banner = <BannerFindInterface>{};
  public dto = BannerFormDto;

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public constructor(
    private readonly bannerService: BannerService,
    public readonly formStoreService: FormStoreService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      this.banner.start = moment().toDate();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.bannerService
      .findById<BannerFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (banner) => {
          this.banner = banner;

          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveBanner() {
    if (this.checkValidate(this.banner).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.bannerService.update<BannerFormInterface, BannerFindInterface>(this.banner).subscribe({
          next: (banner) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Баннер "${banner.name}" обновлен!`);
            this.formStoreService.saveNext(banner, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.bannerService.create<BannerFindInterface>(this.banner).subscribe({
          next: (banner) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Баннер создан!');
            this.formStoreService.saveNext(banner, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteBanner() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить баннер "${this.banner.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.bannerService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Баннер "${this.banner.name}" удален!`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public restoreBanner() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить баннер "${this.banner.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.bannerService.restoreOne(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.banner = { ...this.banner, deleted: null } as BannerFindInterface;
            this.errorService.addSuccessMessage(`Баннер "${this.banner.name}" восстановлен!`);
            this.formStoreService.saveNext(this.banner, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public changeColor(color: string) {
    if (color) {
      this.banner.color = color[0] !== '#' ? '#' + color : color;
    } else {
      this.banner.color = '#ff0000';
    }
  }
}
